(() => {
  if (window.__hexieFooterController) {
    window.__hexieFooterController.initAllFooters(document);
    return;
  }

  const instances = new WeakMap();

  const initFooter = (footer) => {
    if (!footer || instances.has(footer)) return;

    const disposers = [];
    let destroyed = false;
    let closeTimer = null;

    const on = (target, type, handler, options) => {
      if (!target?.addEventListener) return;
      target.addEventListener(type, handler, options);
      disposers.push(() => target.removeEventListener(type, handler, options));
    };

    const backToTop = footer.querySelector('[data-hx-back-to-top]');
    const accordions = Array.from(footer.querySelectorAll('[data-hx-footer-accordion]'));
    const mobileFooter = window.matchMedia('(max-width: 989px)');

    const modal = footer.querySelector('[data-hx-cookie-modal]');
    const panel = footer.querySelector('[data-hx-cookie-panel]');
    const openButton = footer.querySelector('[data-hx-cookie-open]');
    const closeButtons = footer.querySelectorAll('[data-hx-cookie-close]');
    const saveButton = footer.querySelector('[data-hx-cookie-save]');
    const allowButton = footer.querySelector('[data-hx-cookie-allow]');
    const rejectButton = footer.querySelector('[data-hx-cookie-reject]');
    const status = footer.querySelector('[data-hx-cookie-status]');

    const consentInputs = {
      preferences: footer.querySelector('[data-hx-consent="preferences"]'),
      analytics: footer.querySelector('[data-hx-consent="analytics"]'),
      marketing: footer.querySelector('[data-hx-consent="marketing"]')
    };

    const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const setAccordionMode = (matches) => {
      accordions.forEach((item) => {
        if (matches) {
          item.removeAttribute('open');
        } else {
          item.setAttribute('open', '');
        }
      });
    };

    accordions.forEach((item) => {
      const summary = item.querySelector('summary');

      on(summary, 'click', (event) => {
        if (!mobileFooter.matches) event.preventDefault();
      });

      on(item, 'toggle', () => {
        if (!mobileFooter.matches || !item.open) return;

        accordions.forEach((other) => {
          if (other !== item) other.removeAttribute('open');
        });
      });
    });

    setAccordionMode(mobileFooter.matches);

    const handleMediaChange = (event) => setAccordionMode(event.matches);

    if (typeof mobileFooter.addEventListener === 'function') {
      mobileFooter.addEventListener('change', handleMediaChange);
      disposers.push(() => mobileFooter.removeEventListener('change', handleMediaChange));
    } else if (typeof mobileFooter.addListener === 'function') {
      mobileFooter.addListener(handleMediaChange);
      disposers.push(() => mobileFooter.removeListener(handleMediaChange));
    }

    on(backToTop, 'click', () => {
      window.scrollTo({ top: 0, behavior: reducedMotion() ? 'auto' : 'smooth' });
    });

    let lastFocus = null;
    let privacyReady = false;

    const lockPage = () => {
      document.documentElement.classList.add('hx-cookie-open');
      document.body.classList.add('hx-cookie-open');
    };

    const unlockPage = () => {
      document.documentElement.classList.remove('hx-cookie-open');
      document.body.classList.remove('hx-cookie-open');
    };

    const loadPrivacy = (callback) => {
      if (privacyReady && window.Shopify?.customerPrivacy) {
        if (!destroyed) callback();
        return;
      }

      if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
        if (status) status.textContent = 'Cookie controls are temporarily unavailable.';
        if (!destroyed) callback();
        return;
      }

      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        (error) => {
          if (destroyed) return;
          privacyReady = !error && Boolean(window.Shopify.customerPrivacy);
          if (error && status) status.textContent = 'Cookie controls are temporarily unavailable.';
          callback();
        }
      );
    };

    const readConsent = () => {
      const api = window.Shopify?.customerPrivacy;
      if (!api || typeof api.currentVisitorConsent !== 'function') return;

      const current = api.currentVisitorConsent();

      Object.keys(consentInputs).forEach((key) => {
        if (consentInputs[key]) consentInputs[key].checked = current?.[key] === 'yes';
      });
    };

    const openModal = () => {
      if (!modal || !panel) return;

      lastFocus = document.activeElement;
      modal.hidden = false;
      lockPage();
      if (status) status.textContent = '';

      loadPrivacy(() => {
        readConsent();
        window.requestAnimationFrame(() => {
          if (!destroyed && !modal.hidden) panel.focus();
        });
      });
    };

    const closeModal = () => {
      if (!modal || modal.hidden) return;

      modal.hidden = true;
      unlockPage();

      if (lastFocus && typeof lastFocus.focus === 'function' && document.contains(lastFocus)) {
        lastFocus.focus();
      }
    };

    const setConsent = (values) => {
      const api = window.Shopify?.customerPrivacy;

      if (!api || typeof api.setTrackingConsent !== 'function') {
        if (status) status.textContent = 'Cookie controls are temporarily unavailable.';
        return;
      }

      if (status) status.textContent = 'Saving your choices…';

      api.setTrackingConsent(
        {
          preferences: Boolean(values.preferences),
          analytics: Boolean(values.analytics),
          marketing: Boolean(values.marketing)
        },
        () => {
          if (destroyed) return;
          if (status) status.textContent = 'Your cookie choices have been saved.';

          Object.keys(consentInputs).forEach((key) => {
            if (consentInputs[key]) consentInputs[key].checked = Boolean(values[key]);
          });

          closeTimer = window.setTimeout(closeModal, 550);
        }
      );
    };

    on(openButton, 'click', openModal);
    closeButtons.forEach((button) => on(button, 'click', closeModal));

    on(saveButton, 'click', () => {
      loadPrivacy(() => {
        setConsent({
          preferences: Boolean(consentInputs.preferences?.checked),
          analytics: Boolean(consentInputs.analytics?.checked),
          marketing: Boolean(consentInputs.marketing?.checked)
        });
      });
    });

    on(allowButton, 'click', () => {
      loadPrivacy(() => setConsent({ preferences: true, analytics: true, marketing: true }));
    });

    on(rejectButton, 'click', () => {
      loadPrivacy(() => setConsent({ preferences: false, analytics: false, marketing: false }));
    });

    const handleKeydown = (event) => {
      if (!modal || modal.hidden) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
        return;
      }

      if (event.key !== 'Tab' || !panel) return;

      const focusable = Array.from(
        panel.querySelectorAll('button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])')
      ).filter((element) => !element.hidden && element.offsetParent !== null);

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    on(document, 'keydown', handleKeydown);

    const destroy = () => {
      if (destroyed) return;
      destroyed = true;

      if (closeTimer) window.clearTimeout(closeTimer);
      if (modal && !modal.hidden) unlockPage();

      disposers.splice(0).forEach((dispose) => dispose());
      instances.delete(footer);
    };

    instances.set(footer, { destroy });
  };

  const getFooters = (root) => {
    if (!root) return [];
    const footers = [];
    if (root.matches?.('[data-hx-footer]')) footers.push(root);
    root.querySelectorAll?.('[data-hx-footer]').forEach((footer) => footers.push(footer));
    return footers;
  };

  const initAllFooters = (root = document) => {
    getFooters(root).forEach(initFooter);
  };

  const destroyFooters = (root) => {
    getFooters(root).forEach((footer) => instances.get(footer)?.destroy());
  };

  const handleSectionLoad = (event) => initAllFooters(event.target);
  const handleSectionUnload = (event) => destroyFooters(event.target);

  document.addEventListener('shopify:section:load', handleSectionLoad);
  document.addEventListener('shopify:section:unload', handleSectionUnload);

  window.__hexieFooterController = {
    initAllFooters,
    destroyFooters
  };

  initAllFooters();
})();
